<template>
    <div class="survey-input-field">
        <div class="q-mb-md">
            <NumberInputWithValidation
                ref="inputComponentRef"
                v-model="inputValue"
                :field-name="`question-${question.questionId}`"
                type="number"
                :rules="validationRules"
                :outlined="false"
                :input-width="FormElWidthEnum.qFormEl125"
            />
            <div v-if="inputFieldConfig.unitsLabel" class="q-mt-xs text-caption text-grey-7">
                {{ inputFieldConfig.unitsLabel }}
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { computed, ref } from 'vue';
    import type { SurveyQuestionModel } from '@/types/model';
    import { useSurveyStore } from '@/store';
    import NumberInputWithValidation from '@/components/form/NumberInputWithValidation.vue';
    import { useI18n } from 'vue-i18n';
    import { FormElWidthEnum } from '@/constants/enums';

    const { t } = useI18n();
    const surveyModule = useSurveyStore();

    const props = defineProps<{
        question: SurveyQuestionModel;
        inputFieldConfig: {
            valueType: string;
            range: { start: number; end: number };
            unitsLabel: string;
        };
    }>();

    const selectedValue = defineModel();
    const inputComponentRef = ref<any>(null);

    const inputValue = computed({
        get(): number | null {
            const pageOrder = surveyModule.currentPage?.renderingConfig?.order;
            if (pageOrder === undefined) return null;
            return selectedValue.value?.[pageOrder]?.[props.question.questionId] ?? null;
        },
        set(newVal: number | null) {
            const pageOrder = surveyModule.currentPage?.renderingConfig?.order;
            if (pageOrder === undefined) return;
            
            if (!selectedValue.value[pageOrder]) {
                selectedValue.value[pageOrder] = {};
            }

            // Store null for empty values, otherwise store the number
            const valueToStore = (newVal === null || newVal === undefined || newVal === '') ? null : newVal;
            selectedValue.value[pageOrder][props.question.questionId] = valueToStore;
        }
    });

    const validationRules = computed(() => [
        (val: any) => {
            const range = props.inputFieldConfig?.range;
            const canSkip = props.question?.renderingConfig?.canSkip ?? false;
            const isEmpty = val == null || (typeof val === 'string' && val.trim() === '');

            if (canSkip && isEmpty) {
                return true;
            }

            const numVal = typeof val === 'string' ? parseFloat(val) : val;

            if ((!isEmpty && !Number.isInteger(numVal))) {
                return t('survey_integer');
            }
            
            if (range) {
                if (range?.start != null && numVal < range?.start) {
                    return t('error_lower_than_min_pwe', [range.start]);
                }
                if (range?.end != null && numVal > range?.end) {
                    return t('error_higher_than_min_pwe', [range.end]);
                }
            }

            return true;
        },
    ]);

    const validate = async () => {
        if (inputComponentRef.value) {
            return await inputComponentRef.value.validate();
        }
        
        return true;
    };

    defineExpose({
        validate
    });
</script>